import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  MessageCircle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  LogOut,
  Smartphone,
  Loader2,
  AlertCircle,
  Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { whatsappApi } from "@/lib/crmApi";
import { cn } from "@/lib/utils";

const STATUS_META = {
  connected: {
    label: "Connecté",
    color: "bg-emerald-500",
    text: "text-emerald-700 dark:text-emerald-300",
    bg: "bg-emerald-50 dark:bg-emerald-950/40",
    border: "border-emerald-300 dark:border-emerald-900/50",
    Icon: CheckCircle2,
  },
  qr: {
    label: "En attente du scan",
    color: "bg-amber-500",
    text: "text-amber-700 dark:text-amber-300",
    bg: "bg-amber-50 dark:bg-amber-950/40",
    border: "border-amber-300 dark:border-amber-900/50",
    Icon: Smartphone,
  },
  connecting: {
    label: "Connexion en cours…",
    color: "bg-blue-500",
    text: "text-blue-700 dark:text-blue-300",
    bg: "bg-blue-50 dark:bg-blue-950/40",
    border: "border-blue-300 dark:border-blue-900/50",
    Icon: Loader2,
  },
  disconnected: {
    label: "Déconnecté",
    color: "bg-zinc-400",
    text: "text-zinc-700 dark:text-zinc-300",
    bg: "bg-zinc-100 dark:bg-zinc-800/60",
    border: "border-zinc-300 dark:border-zinc-700",
    Icon: XCircle,
  },
  logged_out: {
    label: "Déconnecté (manuellement)",
    color: "bg-zinc-400",
    text: "text-zinc-700 dark:text-zinc-300",
    bg: "bg-zinc-100 dark:bg-zinc-800/60",
    border: "border-zinc-300 dark:border-zinc-700",
    Icon: LogOut,
  },
  conflict: {
    label: "Conflit — numéro lié ailleurs",
    color: "bg-orange-500",
    text: "text-orange-700 dark:text-orange-300",
    bg: "bg-orange-50 dark:bg-orange-950/40",
    border: "border-orange-300 dark:border-orange-900/50",
    Icon: AlertCircle,
  },
};

/**
 * Pairing/status panel for a Baileys session.
 *
 * Props
 *   - sessionId: project ID for per-project sessions, or null/undefined for
 *     the legacy "default" session (single global number).
 *   - projetNom: display label shown in the header when sessionId is set.
 *   - onStart / onDelete: optional callbacks fired after start/delete so a
 *     parent component can refresh its session list.
 */
export default function WhatsAppConnect({
  sessionId = null,
  projetNom = null,
  onStart = null,
  onDelete = null,
} = {}) {
  const [status, setStatus] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serviceError, setServiceError] = useState(null);
  const [busy, setBusy] = useState(false);
  // For a project-scoped session that has never been started, the sidecar
  // returns 404 from /status. We surface that as a "not started yet" state
  // with a single big "Démarrer" button instead of a scary error.
  const [needsStart, setNeedsStart] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const s = await whatsappApi.status(sessionId);
      setStatus(s);
      setServiceError(null);
      setNeedsStart(false);
      if (s.has_qr) {
        try {
          const r = await whatsappApi.qr(sessionId);
          setQrUrl(r.qr || null);
        } catch {
          setQrUrl(null);
        }
      } else {
        setQrUrl(null);
      }
    } catch (e) {
      // 404 on a project session = "session_not_found" → first-time setup
      const status404 =
        e?.response?.status === 404 ||
        (e?.humanMessage || "").toLowerCase().includes("session_not_found");
      if (sessionId && status404) {
        setNeedsStart(true);
        setStatus(null);
        setServiceError(null);
      } else {
        setServiceError(e?.humanMessage || "Service WhatsApp indisponible");
        setStatus(null);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  // Initial load + smart polling. Re-trigger when sessionId changes so the
  // panel correctly switches between project sessions without a remount.
  useEffect(() => {
    setLoading(true);
    refresh();
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, [refresh]);

  const onStartSession = async () => {
    if (!sessionId) return;
    setBusy(true);
    try {
      await whatsappApi.startSession(sessionId);
      toast.success("Session démarrée · QR à scanner sous peu");
      onStart?.(sessionId);
      setTimeout(refresh, 1500);
    } catch (e) {
      toast.error(e?.humanMessage || "Erreur lors du démarrage");
    } finally {
      setBusy(false);
    }
  };

  const onRestart = async () => {
    setBusy(true);
    try {
      // After a manual logout (or a session WhatsApp killed remotely) the
      // stored credentials are dead. /restart alone would re-read them and
      // immediately fail with "Connection Failure" — call /reset instead so
      // the auth dir is wiped first and a fresh QR is produced.
      const needsReset =
        status?.status === "logged_out" ||
        status?.status === "conflict" ||
        (status?.status === "disconnected" && !!status?.last_error);
      if (needsReset) {
        await whatsappApi.reset(sessionId);
      } else {
        await whatsappApi.restart(sessionId);
      }
      toast.success("Service relancé · QR à scanner sous peu");
      setTimeout(refresh, 2000);
    } catch (e) {
      toast.error(e?.humanMessage || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const onReset = async () => {
    if (
      !window.confirm(
        "Réinitialiser la session WhatsApp ? Toutes les credentials locales seront effacées et vous devrez scanner un nouveau QR."
      )
    )
      return;
    setBusy(true);
    try {
      await whatsappApi.reset(sessionId);
      toast.success("Session réinitialisée · scannez le QR");
      setTimeout(refresh, 2000);
    } catch (e) {
      toast.error(e?.humanMessage || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const onLogout = async () => {
    if (!window.confirm("Déconnecter WhatsApp ? Vous devrez scanner un nouveau QR pour vous reconnecter.")) return;
    setBusy(true);
    try {
      await whatsappApi.logout(sessionId);
      toast.success("Numéro déconnecté");
      setTimeout(refresh, 1000);
    } catch (e) {
      toast.error(e?.humanMessage || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  // Permanently remove the session (project-scoped only). Wipes the auth
  // dir on the sidecar; the project goes back to using the default number.
  const onDeleteSession = async () => {
    if (!sessionId) return;
    if (
      !window.confirm(
        `Supprimer définitivement la session WhatsApp de « ${
          projetNom || sessionId
        } » ?\n\nLe numéro sera dissocié du projet. Vous pourrez recréer une session plus tard.`
      )
    )
      return;
    setBusy(true);
    try {
      await whatsappApi.deleteSession(sessionId);
      toast.success("Session supprimée");
      onDelete?.(sessionId);
      setNeedsStart(true);
      setStatus(null);
      setQrUrl(null);
    } catch (e) {
      toast.error(e?.humanMessage || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Chargement de l'état WhatsApp…</span>
        </div>
      </div>
    );
  }

  // First-time setup for a project session — the sidecar doesn't know
  // this session_id yet, so we offer a single CTA to provision and pair it.
  if (needsStart && sessionId) {
    return (
      <div className="rounded-2xl border border-emerald-300 bg-emerald-50/60 p-6 shadow-soft dark:border-emerald-900/40 dark:bg-emerald-950/30">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500 text-white shadow-soft">
            <Smartphone className="h-6 w-6" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold">
              Numéro WhatsApp dédié pour{" "}
              <span className="text-emerald-700 dark:text-emerald-300">
                {projetNom || sessionId}
              </span>
            </h3>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              Connectez un numéro WhatsApp spécifique à ce projet pour que les
              messages welcome, les relances et l'IA partent depuis ce numéro
              plutôt que du numéro global.
            </p>
          </div>
          <Button
            onClick={onStartSession}
            disabled={busy}
            size="lg"
            className="gap-2 bg-emerald-600 hover:bg-emerald-700"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Smartphone className="h-4 w-4" />
            )}
            Démarrer la connexion
          </Button>
          <p className="text-[11px] text-muted-foreground">
            Vous obtiendrez un QR code à scanner depuis l'app WhatsApp du
            téléphone que vous souhaitez utiliser pour ce projet.
          </p>
        </div>
      </div>
    );
  }

  // Service down — no Node sidecar reachable
  if (serviceError) {
    return (
      <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 dark:border-amber-900/40 dark:bg-amber-950/40">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 flex-none text-amber-600" />
          <div>
            <h3 className="font-heading font-bold">Service WhatsApp non démarré</h3>
            <p className="mt-1 text-xs text-muted-foreground">{serviceError}</p>
            <p className="mt-2 text-xs text-muted-foreground">
              Sur le serveur, vérifiez : <code className="rounded bg-background px-1 py-0.5 text-[11px]">systemctl status sogi-crm-wa</code>
            </p>
            <Button onClick={refresh} size="sm" variant="outline" className="mt-3 gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" />
              Réessayer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const meta = STATUS_META[status.status] || STATUS_META.disconnected;
  const Icon = meta.Icon;
  const phone = status.me ? formatJid(status.me) : null;

  return (
    <div className="space-y-4">
      {/* Status pill */}
      <div className={cn("rounded-2xl border p-5 shadow-soft transition-colors", meta.border, meta.bg)}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div className={cn("flex h-10 w-10 flex-none items-center justify-center rounded-xl text-white shadow-soft", meta.color)}>
              <Icon className={cn("h-5 w-5", status.status === "connecting" && "animate-spin")} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-emerald-600" />
                <h3 className="font-heading text-lg font-bold">
                  WhatsApp Business
                  {sessionId && projetNom && (
                    <span className="ml-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                      · {projetNom}
                    </span>
                  )}
                </h3>
              </div>
              <p className={cn("mt-0.5 text-sm font-semibold", meta.text)}>{meta.label}</p>
              {phone && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Numéro connecté : <span className="font-medium text-foreground">{phone}</span>
                </p>
              )}
              {status.connected_since && status.status === "connected" && (
                <p className="text-[11px] text-muted-foreground">
                  Depuis le {new Date(status.connected_since).toLocaleString("fr-FR")}
                </p>
              )}
              {status.last_error && status.status !== "connected" && (
                <p className="mt-1 text-xs text-red-600">{status.last_error}</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={refresh} variant="ghost" size="sm" className="gap-1.5" disabled={busy}>
              <RefreshCw className={cn("h-3.5 w-3.5", busy && "animate-spin")} />
              Actualiser
            </Button>
            {(status.status === "disconnected" || status.status === "logged_out" || status.status === "conflict") && (
              <Button onClick={onRestart} variant="outline" size="sm" className="gap-1.5" disabled={busy}>
                Reconnecter
              </Button>
            )}
            {(status.status === "disconnected" || status.status === "logged_out" || status.status === "qr" || status.status === "conflict") && (
              <Button onClick={onReset} variant="ghost" size="sm" className="gap-1.5 text-amber-600" disabled={busy}>
                Réinitialiser
              </Button>
            )}
            {status.status === "connected" && (
              <Button onClick={onLogout} variant="outline" size="sm" className="gap-1.5 text-red-600" disabled={busy}>
                <LogOut className="h-3.5 w-3.5" />
                Déconnecter
              </Button>
            )}
            {sessionId && (
              <Button
                onClick={onDeleteSession}
                variant="ghost"
                size="sm"
                className="gap-1.5 text-red-600"
                disabled={busy}
                title="Supprimer définitivement la session pour ce projet"
              >
                Supprimer
              </Button>
            )}
          </div>
        </div>

        {/* Daily quota */}
        {status.status === "connected" && (
          <div className="mt-4 rounded-xl border border-border/60 bg-background/60 p-3">
            <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Activity className="h-3 w-3" />
                Quota journalier sortant
              </span>
              <span className="tabular-nums font-medium text-foreground">
                {status.daily_count} / {status.daily_cap}
              </span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className={cn(
                  "h-full transition-all",
                  status.daily_count / status.daily_cap < 0.7
                    ? "bg-emerald-500"
                    : status.daily_count / status.daily_cap < 0.9
                    ? "bg-amber-500"
                    : "bg-red-500"
                )}
                style={{ width: `${Math.min(100, (status.daily_count / status.daily_cap) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Conflict explainer — number linked elsewhere */}
      {status.status === "conflict" && (
        <div className="rounded-2xl border border-orange-300 bg-orange-50 p-5 dark:border-orange-900/40 dark:bg-orange-950/40">
          <h3 className="font-heading flex items-center gap-2 text-sm font-bold text-orange-800 dark:text-orange-200">
            <AlertCircle className="h-4 w-4" />
            Conflit de connexion — ce numéro est lié ailleurs
          </h3>
          <p className="mt-2 text-xs text-orange-800/90 dark:text-orange-200/90">
            WhatsApp refuse la liaison car ce numéro est déjà utilisé par une
            autre session. Pour récupérer le QR :
          </p>
          <ol className="mt-2 space-y-1 text-xs text-orange-800/90 dark:text-orange-200/90">
            <li>
              1. Sur le téléphone du numéro : WhatsApp → <strong>Appareils liés</strong> →
              déliez toute session « SOGI CRM ».
            </li>
            <li>
              2. Vérifiez qu'<strong>aucun autre projet</strong> de la CRM n'utilise ce
              même numéro.
            </li>
            <li>
              3. Cliquez <strong>Réinitialiser</strong> ci-dessus, puis scannez le
              nouveau QR.
            </li>
          </ol>
        </div>
      )}

      {/* QR code panel */}
      {status.status === "qr" && qrUrl && (
        <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
          <h3 className="font-heading flex items-center gap-2 text-sm font-bold">
            <Smartphone className="h-4 w-4 text-emerald-600" />
            Scannez ce code depuis WhatsApp Business
          </h3>
          <ol className="mt-3 space-y-1 text-xs text-muted-foreground">
            <li>1. Ouvrez WhatsApp Business sur votre téléphone</li>
            <li>2. Allez dans Paramètres → Appareils liés → Lier un appareil</li>
            <li>3. Scannez le QR code ci-dessous</li>
          </ol>
          <div className="mt-4 flex justify-center rounded-xl bg-white p-4">
            <img
              src={qrUrl}
              alt="QR code WhatsApp"
              className="h-[280px] w-[280px]"
              data-testid="wa-qr-code"
            />
          </div>
          <p className="mt-3 text-center text-[11px] text-muted-foreground">
            Le QR change toutes les ~20 secondes — la page se rafraîchit automatiquement.
          </p>
        </div>
      )}

      {/* Help card */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-900/40 dark:bg-blue-950/40">
        <h4 className="text-xs font-semibold text-blue-900 dark:text-blue-200">Conseils pour rester safe</h4>
        <ul className="mt-2 list-inside list-disc space-y-0.5 text-[11px] text-blue-800 dark:text-blue-300">
          <li>Utilisez un numéro <strong>WhatsApp Business dédié</strong> (jamais votre numéro perso)</li>
          <li>N'envoyez pas de messages identiques à plusieurs leads d'affilée</li>
          <li>Quota max <strong>{status?.daily_cap || 200} messages sortants/jour</strong> automatiquement appliqué</li>
          <li>Le service envoie automatiquement un délai humain (1.5–4.5 s) avant chaque message</li>
        </ul>
      </div>
    </div>
  );
}

function formatJid(jid) {
  if (!jid) return null;
  // Baileys returns "221787172828:2@s.whatsapp.net" — strip @domain AND :device
  // suffix BEFORE removing non-digits (otherwise the device id leaks into the
  // displayed phone number, e.g. "+221 78 717 282 82" instead of the correct
  // "+221 78 717 28 28").
  const num = String(jid).split("@")[0].split(":")[0].replace(/[^0-9]/g, "");
  // Senegal: +221 + 9 digits → standard format is "+221 XX XXX XX XX"
  if (num.length === 12 && num.startsWith("221")) {
    return `+221 ${num.slice(3, 5)} ${num.slice(5, 8)} ${num.slice(8, 10)} ${num.slice(10, 12)}`;
  }
  if (num.length >= 11) return `+${num.slice(0, 3)} ${num.slice(3, 5)} ${num.slice(5, 8)} ${num.slice(8, 11)}${num.slice(11) ? " " + num.slice(11) : ""}`;
  return num;
}
